import { Inline, Stack, Text, textVariants, vars } from '@etg/wings';
import { css } from '@eti/styles';
import { date, duration as durationUtil } from '@eti/utils';
import { Clock, DotOutline } from '@phosphor-icons/react';
import { useProperty, useSiteContext, useTranslation } from '@eti/providers';
import { EventSegmentType } from '@eti/schema-types';
import { differenceInMilliseconds } from 'date-fns';
import type { Dispatch, SetStateAction } from 'react';
import TimelineWrapper from '../../../common/TimelineWrapper';
import TimelineRow from '../common/TimelineRow';
import type { ExtendedEventSegmentType } from '../models/models';
import TripEventBadge from '../../../../../../widgets/trip-event/TripEventBadge';
import { useDurationHTML } from '../../../../../../common/time/utils/useDurationHTML';
import ItineraryTripEventContent from './ItineraryTripEventContent';
import ItineraryChangeOfDate from './ItineraryChangeOfDate';
import ItineraryTripDetailsSelfTransferCard from './ItineraryTripDetailsSelfTransferCard';

const timelineRowStyles = (durationInDays: number) => css`
  background-color: ${vars.colors.surface.primary};
  border-radius: ${vars.card.borderRadius} ${vars.card.borderRadius}
    ${durationInDays > 0 ? `0 0` : `${vars.card.borderRadius} ${vars.card.borderRadius}`};
  padding-inline-end: 8px;
`;

const timelineIconStyles = css`
  margin-block: -12px;
`;

interface ItineraryEventSegmentProps {
  segment: ExtendedEventSegmentType;
  setIsItineraryDrawerSelfTransferModalOpen?: Dispatch<SetStateAction<boolean>>;
}

const ItineraryEventSegment = ({
  segment,
  setIsItineraryDrawerSelfTransferModalOpen,
}: ItineraryEventSegmentProps) => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const isVISelfTansferDealEnabled = p('IbeClient.VI.SelfTransferDeal.Enabled');
  const { language, timeFormat } = useSiteContext();
  const { getDurationHTML } = useDurationHTML();

  if (!segment.previousArrival || !segment.nextDeparture) {
    return null;
  }

  const durationInDays = date.durationInDays(
    new Date(segment.previousArrival),
    new Date(segment.nextDeparture),
  );

  const stopDurationInMs = differenceInMilliseconds(segment.nextDeparture, segment.previousArrival);

  const stopDurationText = durationUtil.getDuration(
    segment.duration ?? 0,
    language.code,
    timeFormat,
  );

  const filteredEventSegmentTypes =
    segment.types
      ?.filter((eventSegmentType): eventSegmentType is EventSegmentType =>
        Boolean(eventSegmentType),
      )
      ?.filter((eventSegmentType) => eventSegmentType !== EventSegmentType.Stop) ?? [];

  return (
    <div data-testid={`trip-itinerary-event-segment-${segment.segmentId}`}>
      <TimelineRow className={timelineRowStyles(durationInDays)}>
        <TimelineWrapper
          borderColorAfter={vars.colors.inputs.border}
          borderColorBefore={vars.colors.inputs.border}
          borderStyleAfter="dashed"
          borderStyleBefore="dashed"
          childSize={8}
        >
          <DotOutline aria-hidden className={timelineIconStyles} color="#737373" size={32} />
        </TimelineWrapper>
        <Stack>
          <Inline noWrap spacing={8}>
            <Clock aria-hidden data-testid="stop-icon" size={20} />
            <Text asHtml data-testid="trip-event-STOP-title" variant={textVariants._16_BOLD}>
              {t(
                'CartTripDetails.Stop',
                getDurationHTML(stopDurationText, stopDurationInMs, 'trip-event-STOP-time'),
                segment.previousDestination,
              )}
            </Text>
          </Inline>
          {filteredEventSegmentTypes.length > 0 && (
            <Stack role="list">
              {filteredEventSegmentTypes?.map((type) =>
                isVISelfTansferDealEnabled && type === EventSegmentType.SelfTransfer ? (
                  <ItineraryTripDetailsSelfTransferCard
                    key={type}
                    setIsItineraryDrawerSelfTransferModalOpen={
                      setIsItineraryDrawerSelfTransferModalOpen
                    }
                  />
                ) : (
                  <TripEventBadge
                    content={<ItineraryTripEventContent segment={segment} type={type} />}
                    hasTitle
                    key={type}
                    type={type}
                  />
                ),
              )}
            </Stack>
          )}
        </Stack>
      </TimelineRow>
      {durationInDays > 0 && (
        <ItineraryChangeOfDate
          arrivalDate={segment.nextDeparture}
          durationInDays={durationInDays}
          isDashed
          isDisplayedLast
        />
      )}
    </div>
  );
};

export default ItineraryEventSegment;
